import { GetInvitationResponse, Subscription, UserMembershipView, UserRole } from '@localstack/types';
import { GridComponent, VirtualizedGridList } from '@localstack/ui';
import { Box, Typography, Skeleton, Stack } from '@mui/material';
import React, { ReactElement, useMemo } from 'react';

import { SUB_ACTIVE_STATUSES } from '@localstack/constants';

import { PageGutter } from '~/layouts';
import { InvitationRow } from '~/components/MembersAndLicenses/InvitationRow';
import { MemberRow } from '~/components/MembersAndLicenses/MemberRow';

import { SectionHeader } from './SectionHeader';
import { LicenseAssignmentsByUser, ScimStatus, SeatAvailability } from './types';

const DEFAULT_SCIM_STATUS: ScimStatus = { active: false, idpName: null };

const HEADER_COLUMNS =
  'max-content 1fr minmax(220px, max-content) minmax(160px, max-content) minmax(120px, max-content) minmax(44px, max-content)';
const MEMBER_COLUMNS =
  'max-content minmax(180px, 260px) 1fr minmax(220px, max-content) minmax(160px, max-content) minmax(120px, max-content) minmax(44px, max-content)';
const INVITATION_COLUMNS =
  'max-content max-content 1fr minmax(160px, max-content) minmax(120px, max-content) minmax(44px, max-content)';

type MembersListProps = {
  totalMembersCount: number;
  totalInvitationCount: number;
  isMembersLoading: boolean;
  isInvitationsLoading: boolean;
  members: UserMembershipView[];
  adminCount: number;
  resetScrollKey: string;
  subscriptions: Subscription[];
  assignmentsByUser: LicenseAssignmentsByUser;
  seatsAvailability: SeatAvailability;
  invitations: GetInvitationResponse[];
  canUpdateMembers: boolean;
  canDeleteMembers: boolean;
  onResendInvitation?: (id: string) => Promise<void>;
  onRevokeInvitation: (id: string) => Promise<void>;
  onUpdateMemberRole: (userId: string, role: UserRole) => Promise<void>;
  onRemoveMember: (id: string) => Promise<void>;
  setUserLicenses: (memberId: string, subscriptionIds: string[]) => Promise<void>;
  setEditMemberPermissionsId: (memberId: string) => void;
  onUserClick?: (userId: string) => void;
  scimInfo?: ScimStatus;
};

export const MembersList = ({
  totalMembersCount,
  totalInvitationCount,
  isMembersLoading,
  isInvitationsLoading,
  members,
  adminCount,
  resetScrollKey,
  subscriptions,
  assignmentsByUser,
  seatsAvailability,
  invitations,
  canUpdateMembers,
  canDeleteMembers,
  onResendInvitation,
  onRevokeInvitation,
  onUpdateMemberRole,
  onRemoveMember,
  setUserLicenses,
  setEditMemberPermissionsId,
  onUserClick,
  scimInfo = DEFAULT_SCIM_STATUS,
}: MembersListProps): ReactElement => {
  const subscriptionsById = useMemo(
    () => Object.fromEntries(subscriptions.map((sub) => [sub.id, sub])),
    [subscriptions],
  );

  const activeSubscriptions = useMemo(
    () =>
      Object.fromEntries(
        subscriptions.filter((sub) => SUB_ACTIVE_STATUSES.includes(sub.status)).map((sub) => [sub.id, sub]),
      ),
    [subscriptions],
  );
  return (
    <Box>
      <GridComponent columns={HEADER_COLUMNS} px={7}>
        <Typography variant="caption">Name</Typography>
        <div />
        <Typography variant="caption">License</Typography>
        <Typography variant="caption">Status</Typography>
        <Typography variant="caption">Joined</Typography>
        <div />
      </GridComponent>

      <PageGutter variant="full">
        <SectionHeader title="Active" count={totalMembersCount} isLoading={isMembersLoading} />
      </PageGutter>

      <Box sx={{ overflowX: 'auto' }}>
        {(isMembersLoading || members.length === 0) && (
          <GridComponent columns={MEMBER_COLUMNS} px={7}>
            {isMembersLoading && (
              <React.Fragment>
                <Skeleton
                  variant="circular"
                  sx={{ borderRadius: '50%', width: (theme) => theme.spacing(4), height: (theme) => theme.spacing(4) }}
                />
                <Stack direction="column">
                  <Skeleton variant="text" width={160} sx={{ fontSize: 'inherit' }} />
                  <Skeleton variant="text" width={140} sx={{ fontSize: 'inherit' }} />
                </Stack>
                <div />
                <Skeleton variant="text" width={80} />
                <Skeleton variant="text" width={50} />
                <Skeleton variant="text" width={80} />
                <Skeleton variant="text" width={30} />
              </React.Fragment>
            )}
            {!isMembersLoading && members.length === 0 && (
              <Typography color="text.tertiary">No members matching the criteria</Typography>
            )}
          </GridComponent>
        )}

        {!isMembersLoading && members.length > 0 && (
          <VirtualizedGridList
            items={members}
            columns={MEMBER_COLUMNS}
            resetScrollKey={resetScrollKey}
            getRowKey={(member) => member.id}
            gridProps={{ px: 7 }}
            renderRow={(member) => (
              <MemberRow
                member={member}
                assignedSubscriptions={assignmentsByUser[member.user_id]?.subscriptions ?? []}
                allSubscriptions={subscriptionsById}
                availableSubscriptions={activeSubscriptions}
                availableSubscriptionSeats={seatsAvailability}
                canUpdateMembers={canUpdateMembers}
                canDeleteMembers={canDeleteMembers}
                isLastAdmin={member.membership_details.role === UserRole.ADMIN && adminCount <= 1}
                scimInfo={scimInfo}
                onChangeAssignments={(subscriptionIds) => setUserLicenses(member.user_id, subscriptionIds)}
                onUpdateRole={onUpdateMemberRole}
                onRequestEditPermissions={() => setEditMemberPermissionsId(member.user_id)}
                onRemoveMember={onRemoveMember}
                onUserClick={onUserClick}
              />
            )}
          />
        )}
      </Box>

      <PageGutter variant="full">
        <SectionHeader title="Invited" count={totalInvitationCount} isLoading={isInvitationsLoading} />
      </PageGutter>

      <Box sx={{ overflowX: 'auto' }}>
        {(isInvitationsLoading || invitations.length === 0) && (
          <GridComponent columns={INVITATION_COLUMNS} px={7}>
            {isInvitationsLoading && (
              <React.Fragment>
                <Skeleton
                  variant="circular"
                  sx={{ borderRadius: '50%', width: (theme) => theme.spacing(4), height: (theme) => theme.spacing(4) }}
                />
                <Stack direction="column">
                  <Skeleton variant="text" width={160} sx={{ fontSize: 'inherit' }} />
                </Stack>
                <div />
                <Skeleton variant="text" width={80} />
                <Skeleton variant="text" width={80} />
                <Skeleton variant="text" width={30} />
              </React.Fragment>
            )}
            {!isInvitationsLoading && invitations.length === 0 && (
              <Typography color="text.tertiary">No invitations matching the criteria</Typography>
            )}
          </GridComponent>
        )}

        {!isInvitationsLoading && invitations.length > 0 && (
          <VirtualizedGridList
            items={invitations}
            columns={INVITATION_COLUMNS}
            resetScrollKey={resetScrollKey}
            getRowKey={(invitation) => invitation.id}
            gridProps={{ px: 7 }}
            renderRow={(invitation) => (
              <InvitationRow
                invitation={invitation}
                canDeleteMembers={canDeleteMembers}
                onResendInvitation={onResendInvitation}
                onRevokeInvitation={onRevokeInvitation}
              />
            )}
          />
        )}
      </Box>
    </Box>
  );
};
